/**
 * App-icon badge for the store apps (2026-09-25).
 *
 * The web app already keeps its home-screen badge equal to the unread
 * alert count through the browser Badging API (see App.js). That API does
 * not exist inside the native shell's web view, so the iPhone number and
 * the Android launcher count come from two places instead:
 *   • each push carries the count (backend, services/push_service.py) --
 *     the OS shows it with no app involvement;
 *   • the app sets the badge from the same count whenever it fetches it
 *     (open, resume, poll, alerts read) so the number never sticks.
 *
 * Every function no-ops on the web.
 */
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { IS_NATIVE } from '@/lib/nativeContext';

// Imported statically on purpose: a lazy `import()` of the plugin never
// settled inside the iOS web view (seen in the simulator, 2026-09-25), so
// the badge silently never updated. `registerPlugin` costs nothing on the
// web and only loads its web implementation if a method is ever called
// there, which this file never does.
function plugin() {
  return Capacitor.isNativePlatform() ? Badge : null;
}

/** Set the icon badge to `count`; 0 removes it. */
export async function setNativeBadge(count) {
  try {
    const badge = plugin();
    if (!badge) return;
    // P0218: the plugin's set and clear ask iOS for permission (for the badge only) if iOS has not asked yet. On a new
    // install that request could reach iOS before the push sign-up's; iOS then kept the person's "Allow" for the badge
    // only (no sounds) and told the push sign-up "no", so that start did not sign up (seen in the simulator). So the
    // badge waits until notifications are allowed; the push sign-up (nativePush.js) asks for all three. Android always
    // answers "granted" here.
    const { display } = await badge.checkPermissions();
    if (display !== 'granted') return;
    const n = Number(count) || 0;
    if (n > 0) await badge.set({ count: n });
    else await badge.clear();
  } catch (_) { /* launcher without badge support */ }
}

export function clearNativeBadge() {
  return setNativeBadge(0);
}

/**
 * Run `callback` each time the native app returns to the foreground.
 * Returns a cleanup function. No-op on the web (the page's own
 * visibility handling covers that).
 */
export function onNativeResume(callback) {
  if (!IS_NATIVE) return () => {};
  let handle = null;
  let cancelled = false;
  import('@capacitor/app')
    .then(({ App }) => App.addListener('appStateChange', ({ isActive }) => { if (isActive) callback(); }))
    .then((h) => { if (cancelled) h.remove(); else handle = h; })
    .catch(() => {});
  return () => { cancelled = true; if (handle) handle.remove(); };
}
